/**
 * CalendarioPartite — calendario raggruppato per giornata di una competizione.
 *
 * Estratto da LegaPage (tab Calendario) per riusarlo anche nella pagina
 * `/giornata` (top nav "Calendario"). Andrea 2026-05-18.
 *
 * Mostra:
 *   - counter di stato: totali / giocate / da giocare
 *   - blocchi per giornata con risultati o "vs / da giocare" per le future
 *   - click su una partita → onPartitaClick (es. naviga a /partita/:id)
 */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { type PartitaItem, usePartiteCompetizione } from "../../hooks/useFase5";
import { useSquadre } from "../../hooks/useFase7";
import { useAuthStore } from "../../lib/auth-store";
import { api } from "../../lib/api";
import { tr } from "../../i18n";

interface GiornataLight {
  id: string;
  stagione_id: string;
  numero: number;
  numero_serie_a: number;
  stato: string;
}

interface TurnoKOPrevisto {
  fase: string;
  etichetta: string;
  giornata_id: string;
  turno: number;
  andata_ritorno: boolean;
  accoppiamenti: string[];
}

interface CalendarioPartiteProps {
  /** ID della stagione (per fetchare le giornate). */
  stagioneId: string | null;
  /** ID della competizione (campionato/CL/supercoppa). */
  competizioneId: string | null;
  /** Se true, ogni riga partita è un Link cliccabile a `/partita/:id`. */
  partiteCliccabili?: boolean;
}

export function CalendarioPartite({
  stagioneId,
  competizioneId,
  partiteCliccabili = true,
}: CalendarioPartiteProps) {
  const { data: partite } = usePartiteCompetizione(competizioneId || null);
  const { data: squadre } = useSquadre();

  const isAuth = !!useAuthStore((s) => s.accessToken);
  const { data: giornate } = useQuery({
    queryKey: ["stagione-giornate", stagioneId],
    queryFn: async () =>
      (await api.get<GiornataLight[]>(`/admin/stagioni/${stagioneId}/giornate`)).data,
    enabled: isAuth && !!stagioneId,
  });

  const squadraNomeById = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of squadre ?? []) m.set(s.id, s.nome);
    return m;
  }, [squadre]);
  const nomeSquadra = (id: string): string =>
    squadraNomeById.get(id) ?? tr("{{v1}}…", { v1: id.slice(0, 8) });

  // Giornata "corrente" (Andrea 2026-09-09): la prima non ancora
  // calcolata — quella che si sta giocando o per cui si consegna la
  // formazione. Il suo blocco ha il bordo oro, cosi' scorrendo tutto il
  // calendario si vede subito a che punto siamo.
  const giornataCorrenteId = useMemo(() => {
    const attive = (giornate ?? [])
      .filter((g) => g.stato === "da_giocare" || g.stato === "voti_caricati")
      .sort((a, b) => a.numero - b.numero);
    return attive[0]?.id ?? null;
  }, [giornate]);

  const giornataById = useMemo(() => {
    const m = new Map<string, GiornataLight>();
    for (const g of giornate ?? []) m.set(g.id, g);
    return m;
  }, [giornate]);

  // Andrea 2026-09-08: turni della fase a eliminazione già pianificati
  // ma non ancora sorteggiati (le partite KO nascono solo quando l'admin
  // avanza la fase). Senza questi, in CL il calendario si fermava ai
  // gironi.
  const { data: pianoKO } = useQuery({
    queryKey: ["piano-ko", competizioneId],
    queryFn: async () =>
      (await api.get<TurnoKOPrevisto[]>(`/competizioni/${competizioneId}/piano-ko`)).data,
    enabled: isAuth && !!competizioneId,
    staleTime: 5 * 60 * 1000,
  });

  const partitePerGiornata = useMemo(() => {
    const m = new Map<string, PartitaItem[]>();
    for (const p of partite ?? []) {
      const key = p.giornata_id ?? "_no-giornata";
      const arr = m.get(key) ?? [];
      arr.push(p);
      m.set(key, arr);
    }
    return Array.from(m.entries()).sort(([a], [b]) => {
      if (a === "_no-giornata") return 1;
      if (b === "_no-giornata") return -1;
      const ga = giornataById.get(a);
      const gb = giornataById.get(b);
      return (ga?.numero ?? 999) - (gb?.numero ?? 999);
    });
  }, [partite, giornataById]);

  // Turni KO da mostrare: solo quelli la cui giornata non ha ancora
  // partite vere (dopo l'avanzamento di fase le partite esistono e
  // vengono renderizzate normalmente sopra).
  const koPrevistiPerGiornata = useMemo(() => {
    const conPartite = new Set(
      (partite ?? []).map((p) => p.giornata_id).filter(Boolean) as string[],
    );
    const m = new Map<string, TurnoKOPrevisto[]>();
    for (const k of pianoKO ?? []) {
      if (conPartite.has(k.giornata_id)) continue;
      const arr = m.get(k.giornata_id) ?? [];
      arr.push(k);
      m.set(k.giornata_id, arr);
    }
    return Array.from(m.entries()).sort(([a], [b]) => {
      const ga = giornataById.get(a);
      const gb = giornataById.get(b);
      return (ga?.numero ?? 999) - (gb?.numero ?? 999);
    });
  }, [pianoKO, partite, giornataById]);

  return (
    <div className="space-y-3">
      {/* Counter di stato — visibile sempre che ci siano partite */}
      {partite && partite.length > 0 && (
        <div className="card flex items-center gap-3 flex-wrap text-xs">
          <span className="text-fg-muted uppercase tracking-wider font-mono">
            {tr("Stato calendario:")}
          </span>
          <span>
            <b className="text-accent">{partite.length}</b> {tr("partite tot.")}
          </span>
          <span>
            <b className="text-success">
              {partite.filter((p) => p.punteggio_casa !== null).length}
            </b>{" "}
            giocate
          </span>
          <span>
            <b className="text-info">{partite.filter((p) => p.punteggio_casa === null).length}</b>{" "}
            da giocare
          </span>
        </div>
      )}

      {partite && partite.length === 0 && koPrevistiPerGiornata.length === 0 && (
        <div className="card text-center text-fg-muted p-6">
          {tr("Nessuna partita ancora generata per questa competizione.")}
        </div>
      )}

      {partitePerGiornata.map(([giornataKey, group]) => {
        const g = giornataById.get(giornataKey);
        const label = g ? tr("Giornata {{v1}}", { v1: g.numero }) : tr("Partite non assegnate");
        const sub = g ? tr("Serie A {{v1}}", { v1: g.numero_serie_a }) : null;
        const corrente = !!g && g.id === giornataCorrenteId;
        return (
          <div
            key={giornataKey}
            className={`card p-0 overflow-hidden ${
              corrente ? "border-2 border-accent shadow-hero" : ""
            }`}
          >
            <div className="bg-primary-deep/60 px-4 py-2 flex items-center justify-between border-b border-primary-soft">
              <span className="font-display text-base text-accent">{label}</span>
              <div className="flex items-center gap-2">
                {g?.stato === "calcolata" && (
                  <span className="pill text-[10px] bg-success/20 text-success">
                    {tr("calcolata")}
                  </span>
                )}
                {g?.stato === "voti_caricati" && (
                  <span className="pill text-[10px] bg-info/20 text-info">
                    {tr("voti caricati")}
                  </span>
                )}
                {g?.stato === "da_giocare" && (
                  <span className="pill text-[10px] bg-fg-muted/20 text-fg-muted">
                    {tr("da giocare")}
                  </span>
                )}
                {g?.stato === "annullata" && (
                  <span className="pill text-[10px] bg-danger/30 text-danger">
                    {tr("annullata")}
                  </span>
                )}
                {sub && <span className="text-xs text-fg-muted">{sub}</span>}
              </div>
            </div>
            {(group ?? []).map((p) => {
              const daGiocare = p.punteggio_casa === null;
              const RowContent = (
                <>
                  <span className="flex-1 min-w-0 font-bold truncate text-right">
                    {nomeSquadra(p.squadra_casa_id)}
                  </span>
                  <span
                    className={`w-12 sm:w-20 shrink-0 text-center font-mono font-bold ${
                      daGiocare ? "text-info" : ""
                    }`}
                  >
                    {daGiocare ? "vs" : `${p.gol_casa} - ${p.gol_trasferta}`}
                  </span>
                  <span className="flex-1 min-w-0 font-bold truncate">
                    {nomeSquadra(p.squadra_trasferta_id)}
                  </span>
                  {/* Andrea 2026-09-24: la matita della modifica manuale in
                      una colonnina fissa, se no spostava il risultato. */}
                  <span
                    className="w-3 shrink-0 text-center text-warning text-[10px]"
                    title={p.rettifica ? tr("Risultato modificato manualmente") : undefined}
                  >
                    {p.rettifica ? "✎" : ""}
                  </span>
                  {/* Andrea 2026-09-08: su mobile la colonna fantapunti
                      sparisce, il nome squadra conta di più. */}
                  <span className="hidden sm:block w-24 shrink-0 text-right font-mono text-[10px] text-fg-muted">
                    {daGiocare
                      ? tr("da giocare")
                      : tr("{{v1}} / {{v2}}", {
                          v1: Number(p.punteggio_casa).toFixed(1),
                          v2: Number(p.punteggio_trasferta).toFixed(1),
                        })}
                  </span>
                </>
              );
              const baseClass = `flex gap-1 sm:gap-2 px-2 sm:px-4 py-2 border-b border-primary/10 last:border-b-0 text-xs sm:text-sm items-center ${
                daGiocare ? "bg-info/5" : ""
              }`;
              if (partiteCliccabili) {
                return (
                  <Link
                    key={p.id}
                    to={`/partita/${p.id}`}
                    className={`${baseClass} hover:bg-primary-deep/40 transition`}
                  >
                    {RowContent}
                  </Link>
                );
              }
              return (
                <div key={p.id} className={baseClass}>
                  {RowContent}
                </div>
              );
            })}
          </div>
        );
      })}

      {/* Fase a eliminazione ancora da sorteggiare: mostriamo le giornate
          già pianificate con gli accoppiamenti previsti, così il
          calendario CL non si ferma ai gironi (Andrea 2026-09-08). */}
      {koPrevistiPerGiornata.map(([giornataKey, turni]) => {
        const g = giornataById.get(giornataKey);
        const primo = turni[0];
        const label = g ? tr("Giornata {{v1}}", { v1: g.numero }) : primo.etichetta;
        const corrente = !!g && g.id === giornataCorrenteId;
        return (
          <div
            key={`ko-${giornataKey}`}
            className={`card p-0 overflow-hidden border-dashed ${
              corrente ? "border-2 border-accent shadow-hero" : "border-primary/40"
            }`}
          >
            <div className="bg-primary-deep/40 px-3 sm:px-4 py-2 flex items-center justify-between gap-2 border-b border-primary-soft flex-wrap">
              <span className="font-display text-sm sm:text-base text-accent">
                {label}
                <span className="ml-2 text-fg-muted font-sans text-xs">
                  {primo.etichetta}
                  {primo.andata_ritorno
                    ? tr(" · {{v1}}", { v1: primo.turno === 1 ? "andata" : "ritorno" })
                    : tr(" · gara secca")}
                </span>
              </span>
              <span className="pill text-[10px] bg-warning/20 text-warning shrink-0">
                {tr("da sorteggiare")}
              </span>
            </div>
            {primo.accoppiamenti.map((acc, i) => {
              const [casa, trasferta] = acc.split(" vs ");
              return (
                <div
                  key={i}
                  className="flex gap-1 sm:gap-2 px-2 sm:px-4 py-2 border-b border-primary/10 last:border-b-0 text-xs sm:text-sm items-center text-fg-muted italic"
                >
                  <span className="flex-1 min-w-0 truncate text-right">{casa}</span>
                  <span className="w-12 sm:w-20 shrink-0 text-center font-mono">vs</span>
                  <span className="flex-1 min-w-0 truncate">{trasferta}</span>
                </div>
              );
            })}
            {g && (
              <div className="px-3 sm:px-4 py-1.5 text-[10px] text-fg-muted border-t border-primary/10">
                Serie A {g.numero_serie_a} · gli accoppiamenti reali compaiono quando l'admin fa
                avanzare la fase.
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
